const TRACKING_VALUES_KEY = 'trackingValues';

export type ToolSelectionCartType = 'sp' | 'req' | 'add_to_config';

function hashString(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash).toString(36);
}

/**
 * Session-scoped cart id for tool `selection_details` events (legacy eguidance parity).
 */
export function getToolSelectionCartId(
  type: ToolSelectionCartType = 'sp'
): string {
  if (!import.meta.client) {
    return '';
  }

  if (type === 'add_to_config') {
    return '';
  }

  const mapType = type === 'req' ? 'requestCart' : 'cart';
  const storageKey = `${mapType}_cart_id`;
  const existing = window.sessionStorage.getItem(storageKey);
  if (existing) {
    return existing;
  }

  const session = hashString(`${Date.now()}${navigator.userAgent}`);
  window.sessionStorage.setItem(storageKey, session);
  return session;
}

export function syncToolSelectionCartId(
  type: 'cart' | 'requestCart',
  cartId: string
): void {
  if (!import.meta.client || !cartId) {
    return;
  }
  window.sessionStorage.setItem(`${type}_cart_id`, cartId);
}

export function setToolTrackingValues(
  tool: string,
  value: Record<string, unknown>
): void {
  if (!import.meta.client) {
    return;
  }

  const trackingValues = JSON.parse(
    window.sessionStorage.getItem(TRACKING_VALUES_KEY) || '{}'
  ) as Record<string, Record<string, unknown>>;
  trackingValues[tool] = {
    ...(trackingValues[tool] ?? {}),
    ...value
  };
  window.sessionStorage.setItem(
    TRACKING_VALUES_KEY,
    JSON.stringify(trackingValues)
  );
}

export function getToolTrackingValues(tool: string): Record<string, unknown> {
  if (!import.meta.client) {
    return { ecom_source: deriveEcomSource(tool) };
  }

  const trackingValues = JSON.parse(
    window.sessionStorage.getItem(TRACKING_VALUES_KEY) || '{}'
  ) as Record<string, Record<string, unknown>>;
  trackingValues[tool] = trackingValues[tool] ?? {};
  trackingValues[tool].ecom_source = deriveEcomSource(tool);
  setToolTrackingValues(tool, trackingValues[tool]);
  return trackingValues[tool];
}

function deriveEcomSource(tool: string): string {
  return tool
    .split('_')
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

export function buildToolSelectionDetailsPayload(
  custom: Record<string, unknown>,
  type: ToolSelectionCartType
): {
  event: 'selection_details';
  cart_id: string;
  custom: Record<string, unknown>;
} {
  return {
    event: 'selection_details',
    cart_id: getToolSelectionCartId(type),
    custom
  };
}
